import { queryClient } from '../config/queryClientFactory';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorageKeys';
import { QUERY_KEYS } from '../constants/queryKeys';
import { SEARCH_PARAMS } from '../constants/searchParamNames';
import {
  AnalyticsUtility,
  AnalyticsUtilityConfig,
  Category,
  CouponAnalyticsData,
  DisplayedIn,
  FiltersAnalyticsData,
  FiltersAnalyticsReturnData,
  GenerateSaveCouponData,
  Offer,
  PartnerMinimalInfo,
  SaveCouponPutData,
  SearchAnalyticsData,
  SearchSuggestionResult,
  SingleOffer,
  WindowWithDatalayer,
  YSH_GTM_EVENTS,
  YshGtmEvent
} from '../types/types';
import { isUserLoggedIn } from './auth';

const getUserId = () => {
  if (!isUserLoggedIn()) return '';
  return localStorage.getItem(LOCAL_STORAGE_KEYS.GTM_USER_ID) || '';
};

const getActualTariffName = () => {
  if (!isUserLoggedIn()) return '';
  return localStorage.getItem(LOCAL_STORAGE_KEYS.TP_NAME) || '';
};

const getCarrierType = () => {
  if (!isUserLoggedIn()) return '';
  return localStorage.getItem(LOCAL_STORAGE_KEYS.TYPE) || '';
};

const getCustumerType = () => {
  if (!isUserLoggedIn()) return '';
  return localStorage.getItem(LOCAL_STORAGE_KEYS.LEGAL_TYPE) || '';
};

const getPlanType = () => {
  if (!isUserLoggedIn()) return '';
  return localStorage.getItem(LOCAL_STORAGE_KEYS.PAYMENT_TYPE) || '';
};

const getInteractionType = () => {
  return Boolean(
    (window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
      // @ts-expect-error - Fallback for IOS
      window.navigator.standalone
  ).valueOf()
    ? 'pwa'
    : 'web';
};

const getComboType = () => {
  if (!isUserLoggedIn()) return '';
  return localStorage.getItem(LOCAL_STORAGE_KEYS.COMBO_TYPE) || '';
};

export const pushToAnalytics: AnalyticsUtility = (
  config: AnalyticsUtilityConfig
) => {
  const { eventName, data, searchObj, placement } = config;
  const dataLayerContainer = window as WindowWithDatalayer;

  if ('dataLayer' in dataLayerContainer) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let dataLayerEvent: any = {
      event: eventName,
      user_data: {
        user_id: getUserId(),
        actualTariffName: getActualTariffName(),
        carrierType: getCarrierType(),
        customerType: getCustumerType(),
        interactionType: getInteractionType(),
        comboType: isUserLoggedIn() ? getComboType() : '',
        propertyType: 'ysh',
        planType: getPlanType(),
        search_term: searchObj?.searchTerm,
        number_of_results: searchObj?.numberOfResults
      }
    };

    if (placement) {
      dataLayerEvent.placement = placement;
    }

    if (
      (
        [
          YSH_GTM_EVENTS.SAVE_INTERESTS,
          YSH_GTM_EVENTS.ADD_BRANDS
        ] as YshGtmEvent[]
      ).includes(eventName)
    ) {
      dataLayerEvent = {
        ...dataLayerEvent,
        ecommerce: {
          ...data
        }
      };
    } else {
      dataLayerEvent = {
        ...dataLayerEvent,
        ...data
      };
    }

    dataLayerContainer.dataLayer.push(dataLayerEvent);
  }
};

type MappingFunction<T, U> = (arg: T) => Partial<U>;
type MappingFunctionWithListName<T, U> = (
  arg: T,
  listName?: DisplayedIn
) => Partial<U>;
type AsyncMappingFunction<T, U> = (arg: T) => Promise<Partial<U>>;

const getCouponListIdAndName: MappingFunction<
  string,
  Pick<CouponAnalyticsData, 'coupon_list_id' | 'coupon_list_name'>
> = listName => {
  const location = window.location.pathname;
  const pageName = getPageNameFromLocation(location);
  const impressionLocation = [pageName, listName].join(' - ');

  return {
    coupon_list_name: impressionLocation,
    coupon_list_id: impressionLocation
  };
};

export const getSaveCouponPutData: MappingFunctionWithListName<
  GenerateSaveCouponData,
  SaveCouponPutData
> = data => {
  return {
    ...getCouponListIdAndName(data.listName),
    coupon_brand_category: data.couponBrand ?? '',
    page_location: window.location.toString(),
    combo_type: getComboType()
  };
};

export const mapOfferToAnalyticsObject: MappingFunctionWithListName<
  Offer,
  CouponAnalyticsData
> = (offer, listName) => {
  return {
    coupon_id: offer.id,
    coupon_name: offer.name,
    coupon_code: offer.code ?? '',
    coupon_brand: offer.partner_name,
    coupon_brand_category: offer.partner_category_name,
    coupon_category: offer.partner_category_name ?? '',
    coupon_date: `${offer?.date_start}_${offer?.date_end}`,
    coupon_url: offer.url ?? '',
    ...getCouponListIdAndName(listName as string),
    coupon_price: offer.original_price ?? '',
    coupon_discount: offer.discount_value ?? ''
  };
};

export const mapOffersToAnalyticsArray = (
  offers: Offer[],
  listName?: DisplayedIn
): Partial<CouponAnalyticsData>[] =>
  offers.map(offer => mapOfferToAnalyticsObject(offer, listName));

export const mapSingleOfferToAnalyticsObject: MappingFunctionWithListName<
  SingleOffer,
  CouponAnalyticsData
> = (offer, listName) => {
  const location = window.location.pathname;
  const pageName = getPageNameFromLocation(location);
  const impressionLocation = [pageName, listName].join(' - ');

  return {
    coupon_id: offer.id,
    coupon_name: offer.name,
    coupon_code: offer.code ?? '',
    coupon_brand: offer.partner.name,
    coupon_brand_category: offer.partner_category_name,
    coupon_category: offer.categories[0]?.name ?? '',
    coupon_date: `${offer?.date_start}_${offer?.date_end}`,
    coupon_url: offer.url ?? '',
    coupon_list_id: `${pageName} - Coupon details`,
    coupon_list_name: impressionLocation,
    coupon_price: Number(offer.original_price) ?? '',
    coupon_discount: Number(offer.discount_value) ?? ''
  };
};

export const mapFiltersToAnalyticsObject: AsyncMappingFunction<
  FiltersAnalyticsData,
  FiltersAnalyticsReturnData
> = async filters => {
  const data: FiltersAnalyticsReturnData = {};
  const filterKeys = Object.keys(filters) as Array<keyof FiltersAnalyticsData>;

  for (const filter of filterKeys) {
    const tempVar =
      filters[filter as keyof FiltersAnalyticsData]!.toString().split(',');

    if (filter === 'category') {
      const cachedData = await queryClient.ensureQueryData<Category[]>({
        queryKey: [QUERY_KEYS.GET_CATEGORIES_WITH_COUNT, 'withLimit100']
      })!;
      const categories = tempVar.map(cat => {
        return cachedData.find(c => c.id === cat);
      }) as Category[];
      data[filter] = categories;
    } else if (filter === 'partner') {
      const cachedData = await queryClient.ensureQueryData<
        PartnerMinimalInfo[]
      >({
        queryKey: [QUERY_KEYS.GET_PARTNERS, 'withLimit1000']
      })!;

      const partners = tempVar!.map(cat => {
        return cachedData.find(c => c.id === cat);
      }) as PartnerMinimalInfo[];

      data[filter] = partners;
    } else {
      data[filter] = tempVar;
    }
  }

  return data;
};

export const mapSearchToAnalyticsObject: MappingFunction<
  SearchSuggestionResult,
  SearchAnalyticsData
> = searchParams => {
  const data: Record<string, object[]> = {
    search_results_category: [],
    search_results_brand: []
  };
  searchParams.categories.forEach(category => {
    data.search_results_category.push({
      ['coupon_category']: category.name,
      ['coupon_category_id']: category.id
    });
  });
  searchParams.partners.forEach(category => {
    data.search_results_brand.push({
      ['coupon_brand']: category.name,
      ['coupon_brand_id']: category.id
    });
  });
  return data;
};

export const getPageNameFromLocation = (location: string) => {
  if (location === '/') return 'HomePage';
  if (location === '/ponude') return 'OffersPage';
  if (location.includes('/near-by/partner')) return 'NearByPage';

  return '';
};

export const pushImpressionsToAnalytics = (
  offers: Offer[],
  listName: string
) => {
  const location = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const pageName = getPageNameFromLocation(location);
  const impressionLocation = [pageName, listName].join(' - ');

  const data = {
    coupon_details: [
      ...offers.map(offer => {
        return {
          ...mapOfferToAnalyticsObject(offer),
          coupon_list_name: impressionLocation,
          coupon_list_id: impressionLocation
        };
      })
    ]
  };

  if (offers.length > 0 && !searchParams.has(SEARCH_PARAMS.OFFER_ID)) {
    pushToAnalytics({
      eventName: YSH_GTM_EVENTS.COUPON_IMPRESSION,
      placement: impressionLocation,
      data
    });
  }
};

export const getFiltersAnalyticsObject = async (
  fetchSearchParams: URLSearchParams
) => {
  const filtersObject: Record<string, string | number> = {};
  for (const [key, value] of fetchSearchParams) {
    filtersObject[key] = value;
  }
  const result = await mapFiltersToAnalyticsObject(filtersObject);
  return result;
};
